import {useEffect, useRef, useState} from 'react';
import throttle from 'lodash/throttle';
import {select, pack, hierarchy, HierarchyCircularNode} from 'd3';
import {useNavigate} from 'react-router-dom';

import {
  COLOR_EXTRACTED_26,
  COLOR_EXTRACTED_36,
  COLOR_EXTRACTED_45,
  COLOR_EXTRACTED_53,
  COLOR_EXTRACTED_57,
} from 'src/styles/colors';
import {getImpactScores} from 'src/api/stats';
import {NodeDetailModal} from 'src/components/NodeDetail/NodeDetailModal';
import {LoadingPlaceholder} from 'src/components/LoadingPlaceholder';
import {ContextMenu, MenuItem} from 'src/components/ContextMenu';
import {useContextMenuPosition} from 'src/hooks';
import {DEPENDENCY_PATH, INVESTIGATE_USAGES_PATH} from 'src/routes';


export interface ImpactData {
  index?: number;
  id?: string;
  name?: string;
  shortName?: string;
  type?: string;
  clipUid?: string;
  leafUid?: string;
  value?: number;
  impactScore: number;
  instructionCount?: number;
  methodCount?: number;
  children?: ImpactData;
}

const makePackFn = ({width, height}: {width: number, height: number}) =>
  (data): HierarchyCircularNode<ImpactData> =>
    pack<ImpactData>()
        .size([width, height])
        .padding(3)(
            hierarchy<ImpactData>({children: data, impactScore: 0})
                .sum((d) => (d.value ? d.value : 0)),
        );

const loadImpactScores = async (
    id: string,
    type: string,
): Promise<Array<ImpactData>> => {
  const result: Array<ImpactData> = [];
  const response = await getImpactScores(id, type);

  if (response.status === 'success') {
    response.data?.forEach((i) => {
      result.push({
        id: i.id,
        name: i.name,
        shortName: i.properties['shortName'],
        type: i.type,
        value: i.inboundRelationshipCount,
        impactScore: i.properties['codelogic.quality.impactScore'] ?
                    Math.round(i.properties['codelogic.quality.impactScore'] * 100) :
                    0,
        instructionCount: i.properties['codelogic.quality.instructionCount'],
        methodCount: i.properties['codelogic.quality.methodCount'],
      });
    });

    return result;
  }

  return [];
};

export const ImpactBubbleChartView = (props) => {
  const navigate = useNavigate();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [nodeDetailId, setNodeDetailId] = useState<string | null | undefined>(
      null,
  );
  const [contextId, setContextId] = useState<string | null | undefined>(
      null,
  );
  const [{width, height}, setDimensions] = useState({width: 1, height: 1});
  const [data, setData] = useState<Array<ImpactData>>();

  const [loading, setLoading] = useState<boolean>(false);
  const goToNode = (id, name) => {
    setNodeDetailId(id);
  };

  const {
    contextMenuState,
    showContextMenu,
    hideContextMenu,
  } = useContextMenuPosition();

  useEffect(() => {
    const changeListener = throttle(() => {
      const {current} = containerRef;
      if (!current) return;
      const {width} = current.getBoundingClientRect();
      if (width > 0) {
        setDimensions({
          width,
          height: width,
        });
      }
    }, 100);
    changeListener();

    window.addEventListener('resize', changeListener);
    window.addEventListener('expand-nav', changeListener);
    window.addEventListener('contract-nav', changeListener);
    return () => {
      window.removeEventListener('resize', changeListener);
      window.removeEventListener('expand-nav', changeListener);
      window.removeEventListener('contract-nav', changeListener);
    };
  }, []);


  useEffect(() => {
    const loadData = async () => {
      if (props.id && props.type) {
        setLoading(true);
        const data: Array<ImpactData> = await loadImpactScores(
            props.id,
            props.type,
        );
        setLoading(false);

        data.forEach((d, i) => (d.index = i));
        setData(data);
      }
    };

    loadData();
  }, [props.id, props.type]);

  useEffect(() => {
    const pack = makePackFn({width, height});
    const root = pack(data);

    function wrap(d, i, n) {
      const self = select(n[i]);
      // Conditional required to make test cases work.
      if (self.node().getComputedTextLength) {
        let textLength = self.node().getComputedTextLength();
        let text = self.text();
        const radius = parseInt(self.attr('radius'));
        while (textLength > ((2 * radius) - 2 * 1) && text.length > 0) {
          text = text.slice(0, -1);
          self.text(text + '...');
          textLength = self.node().getComputedTextLength();
        }
      }
    }

    const svgEl = select(svgRef.current);
    svgEl.selectAll('*').remove(); // Clear svg content before adding new elements


    const leaf = svgEl
        .selectAll('a')
        .data(root.leaves())
        .join('a')
        .attr('transform', (d) => `translate(${d.x + 1},${d.y + 1})`)
        .on('click', (e, d) => {
          hideContextMenu();
          setNodeDetailId(d.data.id);
        })
        .on('contextmenu', (e, d) => {
          e.preventDefault();
          setContextId(d.data.id);
          showContextMenu(e.pageX + window.scrollX, e.pageY + window.scrollY);
        })
    ;

    leaf
        .append('circle')
        .attr('id', (d, i) => {
          d.data.leafUid = 'leaf-' + d.data.index + '-' + i;
          return d.data.leafUid;
        })
        .attr('r', (d) => (d.r ? d.r : 0))
        .attr('fill-opacity', 0.7)
        .attr('fill', (d) => {
          let result = COLOR_EXTRACTED_26;
          if (d.data.impactScore > 80) {
            result = COLOR_EXTRACTED_45;
          } else if (d.data.impactScore > 60) {
            result = COLOR_EXTRACTED_53;
          } else if (d.data.impactScore > 40) {
            result = COLOR_EXTRACTED_57;
          } else if (d.data.impactScore > 20) {
            result = COLOR_EXTRACTED_36;
          }
          return result;
        });

    leaf
        .append('clipPath')
        .attr('id', (d, i) => {
          d.data.clipUid = 'clip-' + d.data.index + '-' + i;
          return d.data.clipUid;
        })
        .append('circle')
        .attr('r', (d) => d.r ? d.r : 0);


    leaf
        .append('text')
        .attr(
            'clip-path',
            (d) => 'url(' + window.location.href + '#' + d.data.clipUid,
        )
        .attr('x', (d, i, n) => -(d.r ? d.r : 0) + 7)
        .attr('alignment-baseline', 'start')
        .attr('text-anchor', 'start')
        .attr('radius', (d) => d.r ? d.r : 0)
        .text((d) => (d.data.shortName ? d.data.shortName : (d.data.name ? d.data.name : ''))).each(wrap);

    leaf.append('title').text((d) => {
      return `${
                    d.data.name === undefined ?
                        '' :
                        `Item Name: ${
                          d.data.name +
                            '\n' +
                            'Type: ' +
                            d.data.type +
                            '\n' +
                            'Impact score: ' +
                            d.data.impactScore +
                            '\n' +
                            'Inbound Relationship Count: ' +
                            d.data.value +
                            (d.data.instructionCount ? '\nInstruction Count: ' + d.data.instructionCount : '') +
                            (d.data.methodCount ? '\nMethod Count: ' + d.data.methodCount : '')
                        }
`
      }`;
    });
  }, [data, width, height]);

  return loading ? <LoadingPlaceholder /> : (
    <>
      <div ref={containerRef} style={{display: 'flex', width: '100%'}}>
        <svg
          ref={svgRef}
          width={width}
          height={height}
          textAnchor={'middle'}
        ></svg>
        {nodeDetailId && (
          <NodeDetailModal
            isOpen={nodeDetailId !== null}
            toggle={() => setNodeDetailId(null)}
            nodeId={nodeDetailId}
            goToNode={goToNode}
          />
        )}
      </div>
      <ContextMenu {...contextMenuState} manageEvents={false} positionFixed={true}>
        <MenuItem onClick={() =>navigate(DEPENDENCY_PATH + `?entityId=${contextId}`)}>Explore</MenuItem>
        <MenuItem onClick={() => navigate(INVESTIGATE_USAGES_PATH.replace('/:id/', `/${contextId}/`))}>Investigate Usages</MenuItem>
      </ContextMenu>
    </>
  );
};
